import { sfx } from './audio/sfx';
import { dayNumber, formatDay, formatWait, msUntilNextGrid } from './game/daily';
import { DRILLS, drillById } from './game/drills';
import { LATEST_SOLVER_EPOCH, solverEpochFor } from './game/epochs';
import { generate, generateForDay } from './game/puzzle';
import { mwFor, statsFor } from './game/scoring';
import { GameSession, type TapResult } from './game/session';
import { score } from './game/solver';
import { TOPOLOGY_NAME, classifyTopology } from './game/topology';
import type { DrillResult, Mode, Point, Progress, Puzzle, SolverEpoch } from './game/types';
import { BoardRenderer } from './render/board';
import { celebrate } from './render/confetti';
import {
  prefersReducedMotion,
  setReducedMotionOverride,
  syncReducedMotionAttr,
} from './render/motion';
import {
  bumpStreak,
  emptyProgress,
  hasSeenHelp,
  hasSeenStudyWarning,
  loadContrast,
  loadDrills,
  loadHaptics,
  loadLargeText,
  loadMotionOverride,
  loadMuted,
  loadProgress,
  markHelpSeen,
  markStudyWarningSeen,
  movesFor,
  recordResult,
  recordRun,
  runFor,
  saveContrast,
  saveDrills,
  saveHaptics,
  saveLargeText,
  saveMotionOverride,
  saveMuted,
  saveProgress,
  streakIsLive,
} from './state/progress';
import { buildArchiveRows, renderArchive } from './ui/archive';
import { must, setVisible } from './ui/dom';
import { BUZZ, buzz, hapticsOn, hapticsSupported, setHaptics } from './ui/haptics';
import { hud } from './ui/hud';
import { Overlay, closeOnEscape } from './ui/overlays';
import { ResultPanel, buildResultView } from './ui/result';
import { applyFlag, bindSwitch, flagApplied } from './ui/settings';
import { buildShareText, copyText } from './ui/share';
import { quietToast, toast } from './ui/toast';
import { buildYardRows, renderYard } from './ui/yard';

/** Pause after the surge lands before the result panel slides in. */
const PANEL_DELAY = 420;

/**
 * Tapping the plant or a house is silent — there's nothing to explain.
 * Nudges state the physics and the fix, the right way round: wires cross
 * water and clear peaks; terrain stops towers.
 */
const REJECT_MESSAGE = {
  water: 'Towers need dry ground. Wires can span the water.',
  mtn: 'Too steep for a tower. Wires can clear the peak.',
  'out-of-towers': 'Out of towers. Take one down first.',
} as const;

/**
 * A drill's scoring seed. Derived from its permanent id so a drill's target
 * is the same number for everyone, forever, without a day number to hang it
 * on (FNV-1a, for no reason beyond being short and stable).
 */
function drillSeed(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h = Math.imul(h ^ id.charCodeAt(i), 0x01000193);
  }
  return h >>> 0;
}

/**
 * Wires the game to the page: owns the current session, the board, and the
 * player's saved progress, and keeps the three in agreement.
 */
export class WirespanApp {
  private readonly board: BoardRenderer;
  private readonly panel = new ResultPanel();
  /** Today's grid number. Not readonly: it moves when midnight passes mid-session. */
  private today = dayNumber();

  private studyWarned = false;

  private helpOverlay!: Overlay;
  private archiveOverlay!: Overlay;
  private yardOverlay!: Overlay;
  private studyOverlay!: Overlay;
  private settingsOverlay!: Overlay;

  private progress: Progress = emptyProgress();
  private drills: Record<string, DrillResult> = {};
  private session!: GameSession;
  private mode: Mode = 'daily';
  private playDay = this.today;
  /** Which drill is on the board, or `null` outside the training yard. */
  private drillId: string | null = null;

  constructor(boardSvg: SVGSVGElement) {
    this.board = new BoardRenderer(boardSvg);
  }

  /** Load progress, wire the chrome, and deal today's grid. */
  async start(): Promise<void> {
    await this.loadSettings();
    this.bindControls();
    this.progress = await loadProgress();
    this.drills = await loadDrills();
    this.studyWarned = await hasSeenStudyWarning();
    this.showStreak();
    const seen = await hasSeenHelp();

    this.startDaily(this.today);

    // Keep the card's next-grid line honest while the app sits open.
    setInterval(() => this.tickNextGrid(), 30_000);

    if (!seen) {
      this.helpOverlay.open();
      void markHelpSeen();
    }
  }

  // ---------- grids ----------

  private startDaily(day: number = this.today): void {
    this.mode = 'daily';
    this.playDay = day;
    this.drillId = null;
    // Both epochs are resolved from the grid number, never from the current
    // version of the code: grid #12 deals the map and races the target it has
    // always had (GDD §3.5, §5).
    this.beginSession(generateForDay(day), day, day, solverEpochFor(day));

    // How it was played is restored before what was achieved: `restore()`
    // latches the perfect grid, and flawless has to be on the session by then.
    this.session.restoreRun(runFor(this.progress, day));
    const saved = this.progress.results[day];
    if (saved) {
      this.session.restore(saved.towers, saved.firstMW ?? saved.mw ?? null, saved.mwTrail);
    }

    this.mountAndDraw();
    must('daytag').innerHTML = `Daily grid <strong>#${day}</strong> · ${formatDay(day)}`;
  }

  private startPractice(): void {
    this.mode = 'practice';
    this.drillId = null;
    const seed = 100000 + Math.floor(Math.random() * 899999);
    this.beginSession(generate(seed), seed, null, LATEST_SOLVER_EPOCH);
    this.mountAndDraw();
    must('daytag').innerHTML = `<strong>Practice</strong> · anything goes`;
  }

  /**
   * A drill from the training yard (GDD §4.6): authored, not dealt. No
   * streak, no share, no day number — a lesson, not a result.
   */
  private startDrill(id: string): void {
    const drill = drillById(id);
    if (!drill) return;
    this.mode = 'drill';
    this.drillId = id;
    this.beginSession(drill.puzzle, drillSeed(id), null, LATEST_SOLVER_EPOCH);
    this.mountAndDraw();
    must('daytag').innerHTML = `<strong>Drill</strong> · ${drill.name}`;
  }

  private beginSession(puzzle: Puzzle, seed: number, day: number | null, epoch: SolverEpoch): void {
    this.panel.hide();
    this.board.clearReference();
    hud.setMegawatts(null);
    this.session = new GameSession(score(puzzle, seed, epoch), this.mode, day);
  }

  private mountAndDraw(): void {
    this.board.mount(this.session.puzzle, {
      onCell: (p) => this.onCell(p),
      onEnergized: (order) => sfx.play('energize', order),
      onHouseLit: (order) => sfx.play('houseLit', order),
    });
    this.board.onHoverEnter = (p) => {
      if (this.session.hasTowerAt(p) || this.session.placed.length >= this.session.puzzle.par) {
        return;
      }
      this.board.showGhost(p, this.planCost(p));
    };
    this.setButtons();
    this.refresh();
  }

  /**
   * What the plan would deliver, for the ghost to carry.
   *
   * The number appears exactly where it means something: on a plan that
   * lights every house. Before that there is no MW to project, and a dash
   * riding every cell would be noise on the one affordance that has to stay
   * quiet.
   */
  private planCost(p: Point): string | undefined {
    const stats = statsFor(this.session.puzzle, this.session.placed.concat([p]));
    if (!stats.ok) return undefined;
    return `${mwFor(this.session.puzzle, stats.wire)} MW`;
  }

  /**
   * The controls row is capped at three buttons so it never wraps on a phone
   * (STYLEGUIDE §7): How to play, plus two contextual. Every mode's road runs
   * back through today.
   */
  private setButtons(): void {
    const daily = this.mode === 'daily';
    const onToday = daily && this.playDay === this.today;
    setVisible(must('practiceBtn'), onToday);
    setVisible(must('newMapBtn'), this.mode === 'practice');
    setVisible(must('archiveBtn'), daily);
    setVisible(must('yardBtn'), this.mode === 'drill');
    setVisible(must('todayBtn'), !onToday);
    // A drill is a lesson, not a result: there is nothing to brag about.
    setVisible(must('shareBtn'), this.mode !== 'drill');
    this.syncReveal();
  }

  // ---------- play ----------

  private onCell(p: Point): void {
    const result: TapResult = this.session.tap(p);
    this.board.clearGhost();

    if (result.type === 'rejected') {
      if (result.reason === 'occupied') return;
      if (result.reason === 'out-of-towers') {
        // The reason lives in the HUD, so the flash answers there.
        hud.flashTowerLimit();
        sfx.play('limit');
        buzz(BUZZ.limit);
      } else {
        // The reason is the cell itself, so the pulse answers there.
        sfx.play('deny');
        this.board.pulseDenied(p);
        buzz(BUZZ.deny);
      }
      toast(REJECT_MESSAGE[result.reason]);
      return;
    }

    if (result.type === 'built') {
      sfx.play('build');
      buzz(BUZZ.build);
      this.refresh({ built: result.at });
      this.board.pulseRange(result.at);
    } else {
      sfx.play('remove');
      buzz(BUZZ.remove);
      // The ever-removed flag is written the moment it changes, not on
      // completion, so flawless cannot be laundered by a reload (GDD §3.6).
      this.persistRun();
      this.refresh({ removed: result.at });
    }
  }

  private refresh(opts: { built?: Point; removed?: Point } = {}): void {
    const { net, maxDelay } = this.board.render(this.session.placed, {
      justBuilt: opts.built,
      justRemoved: opts.removed,
    });
    const ev = this.session.evaluate();
    const puzzle = this.session.puzzle;

    hud.setTowers(this.session.placed.length, puzzle.par);
    hud.setHouses(net.houseLinks.filter((h) => h.lit).length, puzzle.houses.length);
    hud.setMegawatts(ev.mw);

    if (ev.justPerfect) this.persistRun();
    if (ev.improved) {
      this.persistBest();
      if (!ev.justSolved) {
        toast(`New best: ${this.session.best?.mw} MW.`);
        if (!ev.justPerfect) {
          sfx.play('newBest');
          hud.flashNewBest();
        }
      }
    }

    // The card is worth staging twice: the first time the grid lights up, and
    // the moment it becomes perfect. Everything else is quiet re-tuning.
    const stage = ev.justSolved || ev.justPerfect;
    if (ev.justSolved) {
      this.board.schedule(() => {
        this.showPanel(ev.mw, { stage: true, perfect: ev.justPerfect });
        this.panel.scrollIntoView(!prefersReducedMotion());
      }, maxDelay + PANEL_DELAY);
    } else if (this.session.solvedOnce) {
      this.showPanel(ev.mw, { stage, perfect: ev.justPerfect });
    }

    this.syncReveal();
    this.tickNextGrid();
  }

  /**
   * The card's quiet footer: a countdown to the next deal on today's grid,
   * flipping to "New grid ready." at local midnight. The board itself never
   * swaps out from under the player (GDD §6.2).
   */
  private tickNextGrid(): void {
    const real = dayNumber();
    if (real !== this.today) {
      // Midnight passed with the app open: surface the way to the new grid
      // and let the streak chip re-evaluate, but leave the board alone. One
      // quiet toast — no cue: nothing electrical happened, and unprompted
      // audio has no place in this mix.
      this.today = real;
      this.setButtons();
      this.showStreak();
      quietToast('New grid ready.');
    }

    const line = must('winNext');
    const relevant = this.mode === 'daily' && this.session.solvedOnce;
    if (relevant && this.playDay === this.today) {
      line.innerHTML = `Next grid in <b>${formatWait(msUntilNextGrid())}</b>.`;
      line.hidden = false;
    } else if (relevant && this.playDay === this.today - 1) {
      // Exactly the rolled-over grid — an archive replay doesn't need telling.
      line.textContent = 'New grid ready.';
      line.hidden = false;
    } else {
      line.hidden = true;
    }
  }

  private showPanel(nowMW: number | null, opts: { stage?: boolean; perfect?: boolean } = {}): void {
    // A count an older version of the game took, on a grid that has one. The
    // counter is retired; the reading it left is still a fact (GDD §4.3).
    const retired = this.mode === 'daily' ? movesFor(this.progress, this.playDay) : null;
    const view = buildResultView(this.session, {
      nowMW,
      streak: this.activeStreak(),
      retiredMoves: retired?.frozen ? retired.count : null,
      topology: this.topologyName(),
      // The lesson retires itself: once a player has perfected any grid, they
      // have the frame these lines exist to give them (GDD §6.2).
      explain: !this.everPerfected(),
    });

    this.panel.show(view, {
      stage: opts.stage,
      onBeat: (index, total) => {
        sfx.play('beat', index);
        if (index < total - 1) return;
        // The last chip lands, then the payoff. Beating the reference is the
        // perfect moment one register brighter, never a second celebration.
        if (opts.perfect) {
          const outdid = this.session.beatReference;
          sfx.play(outdid ? 'engineers' : 'perfect');
          celebrate(this.celebrationOrigin(), { hot: outdid });
          buzz(BUZZ.perfect);
        } else {
          sfx.play('win');
          buzz(BUZZ.win);
        }
      },
    });
  }

  // ---------- the engineers' drawing ----------

  /**
   * The reference is available on a grid whose race is over and which this
   * player finished (GDD §4.5). Today's grid never reveals, at any score, and
   * a grid they have not lit keeps its answer. A drill has no race, so
   * finishing it is the whole gate.
   */
  private canReveal(): boolean {
    // A drill has no race, so finishing it is the whole gate. The stored
    // result is not consulted: the button lives on the result card, which
    // exists only once this session has lit the grid.
    if (this.mode === 'drill') return this.session.solvedOnce;
    // Practice is nobody's race, so finishing it is the whole gate (GDD §4.3).
    if (this.mode === 'practice') return this.session.solvedOnce;
    if (this.mode !== 'daily') return false;
    return this.playDay < this.today && this.session.solvedOnce;
  }

  private syncReveal(): void {
    const btn = must('revealBtn');
    const can = this.canReveal();
    setVisible(btn, can);
    if (!can) this.board.clearReference();
    btn.textContent = this.board.showingReference ? 'Hide reference' : 'Show reference';

    // The second daily ritual: a way through to a finished yesterday, offered
    // only where it exists (GDD §4.3). It can never share the row with the
    // reveal, which is hidden on today's grid by the same rule.
    const onToday = this.mode === 'daily' && this.playDay === this.today;
    setVisible(must('yesterdayBtn'), onToday && Boolean(this.progress.results[this.today - 1]));
  }

  private toggleReveal(): void {
    if (!this.canReveal()) return;
    if (this.board.showingReference) {
      this.board.clearReference();
      this.syncReveal();
      return;
    }
    // Looking before a perfect marks the day. The consequence is stated the
    // first time it can apply, so the mark is never a surprise (GDD §4.3).
    if (!this.session.matchedReference && !this.studyWarned) {
      this.studyOverlay.open();
      return;
    }
    this.showReference();
  }

  private showReference(): void {
    const marks = !this.session.matchedReference;
    this.session.markStudied();
    if (marks) this.persistRun();
    this.board.showReference(this.session.reference.towers);
    this.syncReveal();
  }

  /**
   * The reference's shape, named (GDD §3.5). Computed from the reference
   * layout, so it is the same name for everyone on the same day.
   */
  private topologyName(): string {
    return TOPOLOGY_NAME[classifyTopology(this.session.puzzle, this.session.reference.towers)];
  }

  /** Has this player ever reached a perfect grid on any day? */
  private everPerfected(): boolean {
    return Object.values(this.progress.results).some((r) => r.tier === '🏆');
  }

  /** Burst from the middle of the board, not the middle of the screen. */
  private celebrationOrigin(): { x: number; y: number } | undefined {
    const box = must('board').getBoundingClientRect();
    if (!box.width) return undefined;
    return { x: box.left + box.width / 2, y: box.top + box.height * 0.45 };
  }

  /** The streak, but only when it's this player's live run on today's grid. */
  private activeStreak(): number | null {
    return this.mode === 'daily' && this.playDay === this.today ? this.progress.streak : null;
  }

  /**
   * Save how this grid is being played: the ever-removed flag, flawless, and
   * the study mark. Cheap, idempotent, and called on every change to any of
   * them rather than at the end of the session.
   */
  private persistRun(): void {
    if (this.mode !== 'daily') return;
    this.progress = recordRun(this.progress, this.playDay, this.session.runRecord);
    void saveProgress(this.progress);
  }

  private persistBest(): void {
    const best = this.session.best;
    const tier = this.session.tier();
    if (!best || !tier) return;

    if (this.mode === 'drill') {
      if (!this.drillId) return;
      // A drill keeps no layout, so a replay starts with an empty board and
      // its first solve looks like an improvement. Compare against what is
      // stored, or a worse replay would quietly demote a finished drill.
      const kept = this.drills[this.drillId];
      if (kept && kept.wire <= best.wire) return;
      this.drills = {
        ...this.drills,
        [this.drillId]: { mw: best.mw, wire: best.wire, tier: tier.emoji },
      };
      void saveDrills(this.drills);
      return;
    }
    if (this.mode !== 'daily') return;

    if (this.playDay === this.today) this.progress = bumpStreak(this.progress, this.today);
    this.progress = recordResult(this.progress, this.playDay, {
      towers: best.towers,
      used: best.towers.length,
      mw: best.mw,
      wire: best.wire,
      firstMW: this.session.firstMW,
      tier: tier.emoji,
      mwTrail: this.session.mwTrail,
      // Stored with the result so a tier read back from storage is the tier
      // that was earned, whatever a later epoch's search would say.
      refWire: this.session.puzzle.ref.wire,
    });
    void saveProgress(this.progress);
    this.showStreak();
  }

  /**
   * The streak is today's daily business: it never shows in practice, in the
   * training yard, or on an archive replay (STYLEGUIDE §7).
   */
  private showStreak(): void {
    const onToday = this.mode === 'daily' && this.playDay === this.today;
    const live = onToday && streakIsLive(this.progress, this.today);
    hud.setStreak(live ? this.progress.streak : null);
  }

  // ---------- chrome ----------

  private bindControls(): void {
    // Backdrop and Escape closes have no button to tick for them.
    const dismissTick = () => sfx.play('click');
    this.helpOverlay = new Overlay('helpOverlay', 'helpClose', dismissTick);
    this.archiveOverlay = new Overlay('archOverlay', 'archClose', dismissTick);
    this.yardOverlay = new Overlay('yardOverlay', 'yardClose', dismissTick);
    this.studyOverlay = new Overlay('studyOverlay', 'studyCancel', dismissTick);
    this.settingsOverlay = new Overlay('settingsOverlay', 'settingsClose', dismissTick);
    closeOnEscape([
      this.helpOverlay,
      this.archiveOverlay,
      this.yardOverlay,
      this.studyOverlay,
      this.settingsOverlay,
    ]);
    this.bindSettings();

    must('helpBtn').addEventListener('click', () => this.helpOverlay.open());
    must('practiceBtn').addEventListener('click', () => this.startPractice());
    must('newMapBtn').addEventListener('click', () => this.startPractice());

    must('archiveBtn').addEventListener('click', () => {
      renderArchive(
        buildArchiveRows(this.progress, this.today, this.mode === 'daily' ? this.playDay : null),
        (day) => {
          this.archiveOverlay.close();
          this.startDaily(day);
        },
      );
      this.archiveOverlay.open();
    });

    // The yard is reached from the rules, which is where a player who wants
    // more of them already is. The controls row has no fourth slot.
    const openYard = (): void => {
      this.helpOverlay.close();
      renderYard(buildYardRows(DRILLS, this.drills, this.drillId), (id) => {
        this.yardOverlay.close();
        this.startDrill(id);
      });
      this.yardOverlay.open();
    };
    must('yardOpenBtn').addEventListener('click', openYard);
    must('yardBtn').addEventListener('click', openYard);

    must('todayBtn').addEventListener('click', () => this.startDaily(this.today));
    must('yesterdayBtn').addEventListener('click', () => this.startDaily(this.today - 1));
    must('revealBtn').addEventListener('click', () => this.toggleReveal());
    must('studyConfirm').addEventListener('click', () => {
      this.studyOverlay.close();
      this.studyWarned = true;
      void markStudyWarningSeen();
      this.showReference();
    });

    must('shareBtn').addEventListener('click', () => void this.share());
  }

  /**
   * The settings switches, plus the gesture that lets audio start at all —
   * browsers refuse to open an audio context before the player touches the
   * page. The unlock stays armed for the whole session: after an iOS
   * interruption (backgrounding, a call, Siri) the context sometimes only
   * resumes from inside a gesture, and re-checking is free once it's running.
   */
  private bindSettings(): void {
    document.addEventListener('pointerdown', () => sfx.unlock());

    // Park audio while hidden and nudge it on return. Three events for one
    // moment: pageshow covers Safari's back/forward cache and focus covers
    // the iOS app switches where neither of the others fires — no single
    // event is reliable across every way back to the page.
    const onLifecycle = () => {
      if (document.hidden) sfx.suspend();
      else sfx.wake();
    };
    document.addEventListener('visibilitychange', onLifecycle);
    window.addEventListener('pageshow', onLifecycle);
    window.addEventListener('focus', onLifecycle);

    must('settingsBtn').addEventListener('click', () => this.settingsOverlay.open());

    bindSwitch('setSound', !sfx.muted, (on) => {
      sfx.setMuted(!on);
      void saveMuted(!on);
      // Confirm the change audibly — but only when turning it back on.
      sfx.play('click');
    });

    // The haptics breaker only exists where the API does; iOS never sees it.
    setVisible(must('setHaptics'), hapticsSupported());
    bindSwitch('setHaptics', hapticsOn(), (on) => {
      setHaptics(on);
      void saveHaptics(on);
      sfx.play(on ? 'click' : 'clickOff');
      // Confirm in the channel itself, like the sound switch does.
      if (on) buzz(BUZZ.build);
    });

    const paintMotion = bindSwitch('setMotion', prefersReducedMotion(), (reduced) => {
      setReducedMotionOverride(reduced);
      void saveMotionOverride(reduced);
      this.board.refreshMotion();
      sfx.play(reduced ? 'click' : 'clickOff');
    });
    // The OS preference can flip mid-session; while no override is stored the
    // effective state follows it, so the flag and the switch track along.
    globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').addEventListener('change', () => {
      syncReducedMotionAttr();
      this.board.refreshMotion();
      paintMotion(prefersReducedMotion());
    });

    bindSwitch('setContrast', flagApplied('data-contrast'), (on) => {
      applyFlag('data-contrast', on);
      void saveContrast(on);
      sfx.play(on ? 'click' : 'clickOff');
    });

    bindSwitch('setText', flagApplied('data-large-text'), (on) => {
      applyFlag('data-large-text', on);
      void saveLargeText(on);
      sfx.play(on ? 'click' : 'clickOff');
    });

    // One soft tick for anything else clickable. Board taps have their own
    // sounds, the share button has its own confirmation, and the settings
    // breakers speak for themselves — on and off are different throws.
    document.addEventListener('click', (e) => {
      const target = (e.target as Element | null)?.closest('button');
      if (!target || target.id === 'shareBtn' || target.classList.contains('setting')) return;
      sfx.play('click');
    });
  }

  /** Restore persisted settings before anything can make a noise or animate. */
  private async loadSettings(): Promise<void> {
    sfx.setMuted(await loadMuted());
    setHaptics(await loadHaptics());
    // `null` follows the OS — the sync inside still stamps the effective flag.
    setReducedMotionOverride(await loadMotionOverride());
    applyFlag('data-contrast', await loadContrast());
    applyFlag('data-large-text', await loadLargeText());
  }

  private async share(): Promise<void> {
    const best = this.session.best;
    const tier = this.session.tier();
    if (!best || !tier || this.mode === 'drill') return;

    const matched = this.session.matchedReference;
    const text = buildShareText({
      mode: this.mode,
      day: this.playDay,
      isToday: this.playDay === this.today,
      best,
      firstMW: this.session.firstMW,
      par: this.session.puzzle.par,
      tier,
      beatReference: this.session.beatReference,
      matched,
      archetype: this.session.puzzle.archetype,
      trail: this.session.mwTrail,
      // The name describes the reference, so it stays out of the share until
      // a perfect proves the player already knows it (GDD §6.3).
      topology: matched ? this.topologyName() : null,
      flawless: this.session.flawless,
      studied: this.session.studied,
      streak: this.progress.streak,
    });

    const copied = await copyText(text);
    // A blocked clipboard is a chrome error, not board physics — never the hum.
    sfx.play(copied ? 'copy' : 'copyFail');
    toast(copied ? 'Result copied.' : 'Copy blocked. A screenshot works too.');
  }
}
